import type { IconName } from '../components/ui/Icon';

// Keep in sync with backend/data/emission_factors_ar.csv and
// backend/docs/emissions_methodology.md.

export interface IndicatorInfo {
  icon: IconName;
  title: string;
  formula: string;
  description: string;
}

export interface ElectricityFactorRow {
  year: number;
  factor: string;
  source: string;
  note?: string;
}

export interface LabeledItem {
  label: string;
  description: string;
}

export interface IconItem {
  icon: IconName;
  text: string;
}

export const INDICATORS: readonly IndicatorInfo[] = [
  {
    icon: 'electricity',
    title: 'Electricidad por plato',
    formula: 'kWh del mes ÷ platos servidos',
    description:
      'Detecta picos de consumo y mide el efecto de mejoras de eficiencia.',
  },
  {
    icon: 'gas',
    title: 'Gas por plato',
    formula: 'm³ del mes ÷ platos servidos',
    description: 'Refleja el uso de cocina, calefacción y agua caliente.',
  },
  {
    icon: 'water',
    title: 'Agua por plato',
    formula: 'Litros del mes ÷ platos servidos',
    description: 'Monitorea el uso eficiente del agua en cocina y servicio.',
  },
  {
    icon: 'waste',
    title: 'Residuos por plato',
    formula: 'kg de residuos del mes ÷ platos servidos',
    description: 'Mide la generación de residuos según la actividad real.',
  },
  {
    icon: 'recycle',
    title: 'Desvío de residuos',
    formula: 'kg desviados ÷ kg de residuos totales × 100',
    description: 'Porcentaje de residuos que no va al relleno sanitario.',
  },
  {
    icon: 'eco',
    title: 'CO₂e por plato',
    formula: 'Σ (consumo × factor de emisión) ÷ platos servidos',
    description:
      'Tu huella de carbono de gas (Alcance 1) y electricidad (Alcance 2).',
  },
  {
    icon: 'money',
    title: 'Coste por plato',
    formula: '(Electricidad + Gas + Agua) ÷ platos servidos',
    description:
      'Cuantifica el peso de los servicios en tus costes operativos.',
  },
];

export const ELECTRICITY_FACTORS: readonly ElectricityFactorRow[] = [
  { year: 2025, factor: '0,220', source: 'CAMMESA' },
  { year: 2024, factor: '0,230', source: 'CAMMESA' },
  {
    year: 2023,
    factor: '0,286',
    source: 'SNIGEI-AR',
    note: 'Usa el valor de 2022',
  },
  { year: 2022, factor: '0,286', source: 'SNIGEI-AR' },
  { year: 2021, factor: '0,295', source: 'SNIGEI-AR' },
  { year: 2020, factor: '0,279', source: 'SNIGEI-AR' },
  { year: 2019, factor: '0,276', source: 'SNIGEI-AR' },
  { year: 2018, factor: '0,300', source: 'SNIGEI-AR' },
];

export const GAS_FACTOR = '1,957';

export const SOURCE_LINKS = {
  snigei:
    'https://inventariogei.ambiente.gob.ar/files/Factores_de_Emision_SNIGEIAR_para_HC_2025.pdf',
  cammesa:
    'https://microfe.cammesa.com/static-content/CammesaWeb/download-manager-files/Informe%20Anual/2026/Resumen%20Anual%202025.pdf',
} as const;

export const DATA_SOURCES: readonly LabeledItem[] = [
  {
    label: 'Facturas de servicios',
    description:
      'Electricidad, gas y agua, procesadas automáticamente mediante OCR y revisables antes de guardarse.',
  },
  {
    label: 'Carga manual',
    description:
      'Consumos y residuos (en kg o en bolsas, convertidas a kg según su tamaño).',
  },
  {
    label: 'Platos servidos',
    description:
      'El volumen de actividad que usamos para normalizar cada indicador.',
  },
  {
    label: 'Factores oficiales de Argentina',
    description: 'SNIGEI-AR (Secretaría de Ambiente) y CAMMESA.',
  },
];

export const CALCULATION_RULES: readonly LabeledItem[] = [
  {
    label: 'Prorrateo por días',
    description:
      'Cada factura se reparte por igual entre los días de su período y se suma por mes calendario.',
  },
  {
    label: 'Sin doble conteo',
    description:
      'Facturas duplicadas o solapadas del mismo suministro se depuran antes de calcular.',
  },
  {
    label: 'Unidades normalizadas',
    description:
      'Convertimos MWh, litros, toneladas, etc. Si una unidad no se puede convertir, el registro se marca como "Revisar unidad" y no se incluye.',
  },
  {
    label: 'Factor por año',
    description:
      'Se usa el factor del año del período facturado o, si no existe, el más cercano anterior.',
  },
];

export const PRIVACY_ITEMS: readonly string[] = [
  'Solo procesamos los datos necesarios para generar tus métricas',
  'No compartimos información con terceros sin tu consentimiento',
  'Puedes solicitar la exportación o eliminación de tus datos desde soporte',
  'Aplicamos buenas prácticas de seguridad en el acceso y almacenamiento',
];

export const UPCOMING_ITEMS: readonly IconItem[] = [
  { icon: 'link', text: 'Integración con IoT y medidores inteligentes' },
  { icon: 'chart', text: 'Benchmarking anónimo con otros restaurantes' },
  { icon: 'success', text: 'Certificaciones y reconocimientos ambientales' },
  { icon: 'phone', text: 'App móvil para seguimiento en tiempo real' },
  { icon: 'system', text: 'Sugerencias automáticas de mejora basadas en IA' },
];
